import {
  CloseOutlined,
  DeleteOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Divider, Flex, Space, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import FadeDiv from '@/components/Common/FadeDiv';
import FlexibleTitleInput from '@/components/Inputs/FlexibleTitle';
import { DefaultTabItemStyle } from '@/components/Tab/TabHeader';
import Tabs from '@/components/Tab/Tabs';
import type { Tab } from '@/components/Tab/types';
import useTabs from '@/components/Tab/useTabs';
import useCompanyStore from '@/features/Account/store';
import {
  TabAsyncContentWrapper,
  TabContentStyle,
} from '@/features/Commons/DetailStyles';
import FeedbackTab from '@/features/Commons/FeedbackTab';
import SentimentTab from '@/features/Commons/SentimentTab';
import useColor from '@/hooks/domain/useColor';
import useTrackModal from '@/hooks/domain/useTrackModal';
import useUserRole from '@/hooks/domain/useUserRole';
import useKeymap from '@/hooks/pure/useKeymap';
import service from '@/services/index';
import type { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import usePopupStore from '@/stores/pure/PopupStore';
import useToastStore from '@/stores/pure/ToastStore';
import { detailModalStyle } from '@/styles/style';

import CompanyContactTab from './CompanyContactTab';
import CompanyInfoTab from './CompanyInfoTab';

type ModalProps = {
  pos: number;
  companyAlias: string;
  closeModal: () => void;
  onSelectNewModal: (alias: string, type: DETAIL_MODAL_TYPE) => void;
};

const CompanyDetailModal = (props: ModalProps) => {
  const color = useColor();
  const { isEditor } = useUserRole();
  const setPopup = usePopupStore((state) => state.setPopup);
  const addDefaultToast = useToastStore((state) => state.addDefaultToast);
  const [companyName, setCompanyName] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['company-linked-feedbacks', props.companyAlias],
    queryFn: () => service.account.fetchLinkedFeedback(props.companyAlias),
  });

  const { companyMap, fetchCompany, removeCompany, changeCompanyAttr } =
    useCompanyStore((state) => ({
      companyMap: state.companyMap,
      fetchCompany: state.fetchCompany,
      removeCompany: state.removeCompany,
      changeCompanyAttr: state.changeCompanyAttr,
    }));

  const company = useMemo(() => {
    return companyMap[props.companyAlias];
  }, [companyMap, props.companyAlias]);

  const deleteCompany = () => {
    if (!company) return;
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Delete account',
      message: 'Are you sure you want to delete this account?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        removeCompany(company.alias);
        props.closeModal();
        addDefaultToast('Account Deleted', 'success');
      },
    });
  };

  useEffect(() => {
    if (!company) {
      fetchCompany(props.companyAlias);
    }
  }, [company, fetchCompany, props.companyAlias]);

  useEffect(() => {
    company?.name && setCompanyName(company.name);
  }, [company?.name]);

  useTrackModal({
    domain: 'companies',
    alias: props.companyAlias,
  });

  useKeymap('Escape', props.closeModal);

  const tabs: Tab[] = useMemo(() => {
    return [
      {
        id: 'sentiment',
        text: 'Sentiment',
        content: (
          <TabAsyncContentWrapper status={!isLoading}>
            {data && (
              <SentimentTab
                alias={props.companyAlias}
                data={data}
                onSelectNewModal={props.onSelectNewModal}
              />
            )}
          </TabAsyncContentWrapper>
        ),
      },
      {
        id: 'feedback',
        text: `Feedback (${isLoading ? 0 : data?.feedbacks.length})`,
        content: (
          <TabAsyncContentWrapper status={!isLoading}>
            {data && (
              <FeedbackTab
                alias={props.companyAlias}
                data={data}
                onSelectNewModal={props.onSelectNewModal}
              />
            )}
          </TabAsyncContentWrapper>
        ),
      },

      {
        id: 'account_info',
        text: 'Account Info',
        content: (
          <TabContentStyle>
            <CompanyInfoTab
              companyAlias={props.companyAlias}
              onSelectNewModal={props.onSelectNewModal}
            />
          </TabContentStyle>
        ),
      },
      {
        id: 'contacts',
        text: 'Contacts',
        content: (
          <TabContentStyle>
            <CompanyContactTab
              companyAlias={props.companyAlias}
              onSelectNewModal={props.onSelectNewModal}
            />
          </TabContentStyle>
        ),
      },
    ];
  }, [props.companyAlias, props.onSelectNewModal, isLoading, data]);

  const [selectedTab, selectTab] = useTabs(tabs, props.companyAlias);

  return (
    <ModalStyle status={true} pos={props.pos}>
      <Flex j="space-between" a="center" style={{ padding: '16px 32px' }}>
        <VFlex></VFlex>
        <Flex g="8px">
          {isEditor && (
            <IconButton
              icon={<DeleteOutlined />}
              onClick={deleteCompany}
              tooltip="Delete the account"
            />
          )}
          <IconButton
            icon={<CloseOutlined />}
            onClick={props.closeModal}
            tooltip="Close"
            shortcut="ESC"
          />
        </Flex>
      </Flex>
      <Divider direction="h" />
      <Padded g="16px">
        <Flex g="8px" a="center" style={{ width: '100%' }}>
          {company &&
            (company.favorite ? (
              <StarFilled
                style={{ color: color.yellow500, fontSize: '16px' }}
                onClick={() =>
                  isEditor &&
                  changeCompanyAttr(company.alias, 'favorite', false)
                }
              />
            ) : (
              <StarOutlined
                style={{ color: color.yellow500, fontSize: '16px' }}
                onClick={() =>
                  isEditor && changeCompanyAttr(company.alias, 'favorite', true)
                }
              />
            ))}
          <FlexibleTitleInput
            placeholder="Name"
            style={{ width: 'calc(100% - 40px)' }}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            readOnly={!isEditor}
            onBlur={(e) => {
              if (!company) return;
              changeCompanyAttr(company.alias, 'name', e.target.value);
            }}
          />
        </Flex>
      </Padded>
      <Space h="16px" />
      <Tabs tabs={tabs} selectedTabId={selectedTab}>
        <Tabs.TabHeader
          onSelect={(id) => {
            selectTab(id || null);
          }}
          tabSize="m"
          tabColor="primary"
          scrollEnabled={false}
          tabStyle={StyledCustomTab}
        />

        <Tabs.TabBody />
      </Tabs>
    </ModalStyle>
  );
};

const ModalStyle = styled(FadeDiv)<{ pos: number }>`
  position: fixed;
  width: 600px;
  height: 100vh;
  top: 0;
  right: ${({ pos }) => pos}px;
  ${detailModalStyle}
`;

const Padded = styled(VFlex)`
  padding: 16px 32px 0px 32px;
`;

const StyledCustomTab = styled(DefaultTabItemStyle)`
  max-width: 25%;
  padding: 16px 10px;
`;

export default CompanyDetailModal;
